import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChartPie,
  MessageSquareMore,
  NotebookPen,
  BriefcaseBusiness,
} from "lucide-react";

const FINANCE_FEATURES = [
  {
    title: "손해보험, 생명보험 비교 서비스",
    description: [
      "여러 보험회사의 상품을 선별하고 비교하여 고객의 특성에 맞는 상품을 추천해 드립니다.",
      "굿리치 플러스의 재정설계 컨설턴트는 상품비교를 통해 우수한 상품들만을 선정, 고객에게 제공하고 있습니다.",
    ],
    icon: ChartPie,
  },
  {
    title: "혼합 컨설팅 서비스",
    description: [
      "생명보험과 손해보험은 각각 다른 특성을 가지고 있으며, 상호 보완 관계에 있습니다.",
      "굿리치 플러스의 재정설계 컨설턴트들은 두 가지 상품 특성에 대한 정확한 이해를 바탕으로 고객에게 최상의 컨설팅을 제공하고 있습니다.",
    ],
    icon: BriefcaseBusiness,
  },
  {
    title: "보장분석 서비스",
    description: [
      "현재 가입하고 있는 보험의 내용을 시기별로 분석하여 각종 위험 시 보장받을 수 있는 보험금의 내역을 분석해 드립니다.",
      "개인의 경제적 상황 변화에 맞는 보장을 받고 있는지 정기적인 증권분석 서비스를 통해 확인해 보시기 바랍니다.",
    ],
    icon: MessageSquareMore,
  },
  {
    title: "각종 위험분석 및 종합 보험서비스",
    description: [
      "생명보험을 제외한 모든 영역의 위험에 대한 리스크 분석 및 손해보험을 통한 대안 마련 컨설팅을 제공해 드리고 있습니다.",
      "자동차보험, 화재보험, 여행자 보험, 선박보험, 배상책임 보험 등 모든 영역의 위험에 대해 굿리치 플러스 재정설계 컨설턴트와 의논하시기 바랍니다.",
    ],
    icon: NotebookPen,
  },
];

function FeatureIcon({ icon: Icon }) {
  return (
    <div className="relative flex h-[72px] w-[72px] items-center justify-center sm:h-[84px] sm:w-[84px] md:h-[92px] md:w-[92px]">
      <div className="absolute inset-0 rounded-full bg-[linear-gradient(135deg,rgba(255,150,1,0.24),rgba(24,45,95,0.18))] blur-[1px]" />
      <div className="relative flex h-full w-full items-center justify-center rounded-full border border-[rgba(24,45,95,0.12)] bg-white/92">
        <Icon
          className="h-[48%] w-[48%] text-secondary sm:h-[50%] sm:w-[50%]"
          strokeWidth={1.55}
        />
      </div>
    </div>
  );
}

function FeatureRow({ item, showBorder = true, isFirst = false }) {
  return (
    <article
      className={[
        "grid items-start gap-5 pb-7 sm:gap-6 sm:pb-8 lg:grid-cols-[1fr_108px] lg:gap-8 lg:pb-10",
        !isFirst ? "pt-7 sm:pt-8 lg:pt-10" : "",
        showBorder ? "border-t border-[#dddddd]" : "",
      ].join(" ")}
    >
      <div>
        <h2 className="text-[22px] font-extrabold leading-[1.35] tracking-[-0.04em] text-secondary sm:text-[26px] md:text-[30px] lg:text-[32px]">
          {item.title}
        </h2>

        <div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2">
          {item.description.map((line, idx) => (
            <p
              key={idx}
              className="text-[15px] leading-[1.75] tracking-[-0.02em] text-gray-700 sm:text-[17px] md:text-[18px] lg:text-[20px] lg:leading-[1.8]"
            >
              {line}
            </p>
          ))}
        </div>
      </div>

      <div className="order-first flex justify-start lg:order-none lg:justify-end lg:self-center">
        <FeatureIcon icon={item.icon} />
      </div>
    </article>
  );
}

export default function FinanceServicePage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-white">
      <section className="mx-auto max-w-[1400px] px-5 pb-14 pt-8 sm:px-7 sm:pb-16 sm:pt-10 md:px-10 md:pb-20 md:pt-12 lg:px-12 lg:pb-24 lg:pt-14 xl:px-16">
        <div className="grid items-start gap-8 lg:grid-cols-[260px_1fr] lg:gap-10 xl:grid-cols-[320px_1fr] xl:gap-12">
          <div className="self-start flex w-full flex-row items-center gap-2.5 sm:gap-3 md:gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex shrink-0 items-center text-secondary"
              aria-label="이전 페이지로 이동"
            >
              <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8" />
            </button>

            <h1 className="text-[28px] font-extrabold leading-[1.2] tracking-[-0.04em] text-secondary sm:text-[34px] md:text-[40px] lg:text-[42px] xl:text-[46px]">
              금융 서비스
            </h1>
          </div>

          <div className="mb-6 sm:mb-8 lg:mb-14">
            {FINANCE_FEATURES.map((item, idx) => (
              <FeatureRow
                key={item.title}
                item={item}
                showBorder={idx !== 0}
                isFirst={idx === 0}
              />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
